/**
 * Pressure Testing display helpers: the pressure formatter, the status skin,
 * the kind labels and who may press which write. One file because the vertical
 * is small; split it when it is not.
 *
 * PRESSURE — unlike downhole-units.ts, NOTHING HERE CONVERTS. The API stores
 * kPa and converts to the tenant's unit itself, on read and on write
 * (pressure-testing.routes.ts, `kpaForDisplay` / `displayToKpa`), and says
 * which unit in `meta.pressure_unit`. So the screen prints the figure it was
 * handed with the label it was handed, and an input sends the figure the
 * reader typed. A client-side psi → kPa on submit would be converted a second
 * time by the server and stored 6.89× too large.
 *
 * Display rounds to a WHOLE unit with thousands separators (68,948 kPa,
 * 10,000 psi). The API returns 0.1 precision; that value is what an edit input
 * starts from, so opening and saving a spec does not round it.
 */
import type {
  PtCalibrationStatus,
  PtCertificateKind,
  PtCertificateStatus,
  PtInstrument,
  PtInstrumentKind,
  PtJobStatus,
  PtPressureUnit,
  PtTestResult,
  PtLineKind,
  PtTicket,
  PtTicketLine,
  PtTicketMode,
  PtTicketStatus,
  PtInvoice,
  PtInvoiceStatus,
} from '../types/pressure-testing';
import { fmtCurrency, fmtTaxRate } from './display-labels';
import { formatCalendarDate } from './calendar-date';

function finite(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * A pressure as returned, in the tenant's unit: `10,000 psi`, `68,948 kPa`.
 * Absent → `—` with no unit. An absent unit prints the number alone rather
 * than a guessed label.
 */
export function formatPressure(value: unknown, unit: PtPressureUnit | null | undefined): string {
  const n = finite(value);
  if (n === null) return '—';
  const text = Math.round(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const clean = text === '-0' ? '0' : text;
  return unit ? `${clean} ${unit}` : clean;
}

/** A range, min to max. Both absent → `—`; one absent keeps its own `—`. */
export function formatPressureRange(
  min: unknown,
  max: unknown,
  unit: PtPressureUnit | null | undefined,
): string {
  if (finite(min) === null && finite(max) === null) return '—';
  const lo = formatPressure(min, null);
  const hi = formatPressure(max, null);
  return unit ? `${lo} – ${hi} ${unit}` : `${lo} – ${hi}`;
}

/** The text an edit input opens with: the API's value as written, no separators. Absent → ''. */
export function pressureInputValue(value: unknown): string {
  const n = finite(value);
  return n === null ? '' : String(n);
}

/** What an input sends: the typed figure, or null when blank. NaN when not a number, for the form to refuse. */
export function pressureFromInput(text: string): number | null {
  const t = text.trim().replace(/,/g, '');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

export interface PtStatusSkin {
  label: string;
  fill: string;
  rail: string;
}

/**
 * The API's calibration_status, exhaustive. Overdue red, due soon amber,
 * valid green, never calibrated slate — grey, because "no record" is a gap in
 * the data, not a verdict. Status is the API's answer; nothing here derives it.
 */
export const PT_STATUS_SKIN: Record<PtCalibrationStatus, PtStatusSkin> = {
  overdue: { label: 'Overdue', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  due_soon: { label: 'Due soon', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  valid: { label: 'Valid', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  never: { label: 'Never calibrated', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};

/** An unknown status claims nothing: a slate `—` and a transparent rail, never a real status. */
export const PT_UNKNOWN_SKIN: PtStatusSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

export function ptStatusSkin(value: string | null | undefined): PtStatusSkin {
  return value && Object.prototype.hasOwnProperty.call(PT_STATUS_SKIN, value)
    ? PT_STATUS_SKIN[value as PtCalibrationStatus]
    : PT_UNKNOWN_SKIN;
}

export const PT_KIND_LABEL: Record<PtInstrumentKind, string> = {
  gauge: 'Gauge',
  recorder: 'Recorder',
  pump: 'Test pump',
  transducer: 'Transducer',
};

export function ptKindLabel(value: string | null | undefined): string {
  return value && Object.prototype.hasOwnProperty.call(PT_KIND_LABEL, value) ? PT_KIND_LABEL[value as PtInstrumentKind] : '—';
}

/**
 * The equipment `type` an Add creates, per kind — the same names the API's
 * demo seed uses (print-pressure-testing-seed.ts), so a unit added here and a
 * seeded one read alike on /equipment.
 */
export const PT_KIND_EQUIPMENT_TYPE: Record<PtInstrumentKind, string> = {
  gauge: 'Pressure Gauge',
  recorder: 'Chart Recorder',
  pump: 'Test Pump',
  transducer: 'Pressure Transducer',
};

export const PT_CERT_FORMAT_LABEL: Record<string, string> = {
  absa: 'ABSA',
  generic: 'Generic',
};

/**
 * Who may press which write — mirrors pressure-testing.routes.ts and is not the
 * enforcement. The API's requireRole is a plain membership test (owner is NOT
 * admitted; a dispatcher's admin elevation is a tenant flag this cannot see),
 * so a button these refuse is left off rather than greyed out.
 *
 *   PT_CALIBRATE_ROLES  record a calibration
 *   PT_ADMIN_ROLES      add an instrument, edit specs, tenant settings
 */
export const PT_CALIBRATE_ROLES = ['admin', 'office_staff', 'supervisor'] as const;
export const PT_ADMIN_ROLES = ['admin', 'office_staff'] as const;

export const canCalibratePt = (role: string | null | undefined) =>
  !!role && (PT_CALIBRATE_ROLES as readonly string[]).includes(role);
export const canAdminPt = (role: string | null | undefined) =>
  !!role && (PT_ADMIN_ROLES as readonly string[]).includes(role);

// ─── Bundle 2 (fieldiq-api#915): jobs and tests ──────────────────────────────

/**
 * The job status, exhaustive over PT_JOB_STATUSES. Scheduled blue (committed,
 * not started), active amber (in progress), complete green, cancelled red — the
 * badge is red so a cancelled row reads as cancelled even while greyed out.
 * The rail for cancelled is slate: the row is out of play, not alarming.
 */
export const PT_JOB_STATUS_SKIN: Record<PtJobStatus, PtStatusSkin> = {
  scheduled: { label: 'Scheduled', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  active: { label: 'Active', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  complete: { label: 'Complete', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  cancelled: { label: 'Cancelled', fill: 'bg-fi-signal-red', rail: 'border-l-fi-steel' },
};

export function ptJobStatusSkin(value: string | null | undefined): PtStatusSkin {
  return value && Object.prototype.hasOwnProperty.call(PT_JOB_STATUS_SKIN, value)
    ? PT_JOB_STATUS_SKIN[value as PtJobStatus]
    : PT_UNKNOWN_SKIN;
}

/** A test's result, exhaustive. Pending is slate: no verdict yet, which is a gap, not a state of the well. */
export const PT_RESULT_SKIN: Record<PtTestResult, PtStatusSkin> = {
  pending: { label: 'Pending', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  pass: { label: 'Pass', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  fail: { label: 'Fail', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

export function ptResultSkin(value: string | null | undefined): PtStatusSkin {
  return value && Object.prototype.hasOwnProperty.call(PT_RESULT_SKIN, value)
    ? PT_RESULT_SKIN[value as PtTestResult]
    : PT_UNKNOWN_SKIN;
}

/**
 * Why an instrument cannot go on a test, or null when nothing on its record
 * says it cannot. A PRE-FLIGHT for the picker, not the rule: the API's guard
 * (fieldiq-api src/lib/pt-test-guard.ts) decides, and when it refuses anyway
 * its message is shown as sent.
 *
 * Nothing here is recomputed. The calibration status and days_until_due are
 * the API's (computed against the tenant's day and warn window); the equipment
 * status and range are the API's. This only reads them in the guard's order —
 * out of service, never calibrated, overdue, no range, above range — so the
 * reason shown is the one the guard would give first. `due_soon` is allowed,
 * as the guard allows it.
 *
 * `testPressure` is the typed figure in the tenant's unit, compared with
 * `range_max` in the same unit; null skips the range check (nothing typed yet).
 */
export function ptInstrumentBlockReason(
  i: Pick<PtInstrument, 'status' | 'calibration_status' | 'days_until_due' | 'specs'>,
  testPressure: number | null,
  unit: PtPressureUnit | null,
): string | null {
  if (i.status === 'retired') return 'Retired';
  if (i.status === 'maintenance') return 'In maintenance';
  if (i.calibration_status === 'never') return 'Never calibrated';
  if (i.calibration_status === 'overdue') {
    const days = typeof i.days_until_due === 'number' ? Math.abs(i.days_until_due) : null;
    return days === null ? 'Calibration overdue' : `Calibration overdue ${days} day${days === 1 ? '' : 's'}`;
  }
  const max = finite(i.specs?.range_max);
  if (max === null) return 'No range recorded';
  if (testPressure !== null && Number.isFinite(testPressure) && testPressure > max) {
    return `Above range (max ${formatPressure(max, unit)})`;
  }
  return null;
}

/**
 * Who may press which job write — mirrors fieldiq-api src/lib/pt-roles.ts and
 * is not the enforcement.
 *
 *   PT_DISPATCH_ROLES  create a job, Start / Complete / Cancel, tests on any job
 *   field_worker       tests ONLY on a job whose technician is themself
 *   PT_ADMIN_ROLES     create a test type (the inline "New test type…")
 */
export const PT_DISPATCH_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;

export const canDispatchPt = (role: string | null | undefined) =>
  !!role && (PT_DISPATCH_ROLES as readonly string[]).includes(role);

export const canWritePtTests = (
  user: { role?: string | null; id?: string | null } | null | undefined,
  job: { technician_user_id: string | null } | null | undefined,
) =>
  !!user &&
  !!job &&
  (canDispatchPt(user.role) ||
    (user.role === 'field_worker' && !!job.technician_user_id && job.technician_user_id === user.id));

/**
 * The Monday-to-Sunday week holding `today` (the tenant's day, from
 * `meta.today`), as two 'YYYY-MM-DD' days. Null when `today` is absent or
 * unparseable — the stats then show `—`, never a count against the browser's
 * clock. Built on local construction so the day never shifts across UTC.
 */
export function ptWeekBounds(today: string | null | undefined): { from: string; to: string } | null {
  const m = typeof today === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(today) : null;
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const back = (d.getDay() + 6) % 7; // Monday = 0
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - back);
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (x: Date) => `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
  return { from: fmt(monday), to: fmt(sunday) };
}

// ─── PT-4 (fieldiq-api#921): certificates ────────────────────────────────────

/**
 * The certificate's status, exhaustive over PT_CERTIFICATE_STATUSES. Draft is
 * amber because it is the one waiting on a person (the "Awaiting issue"
 * chip); issued green; rejected red — returned to the technician, with a
 * reason, until someone rebuilds it.
 */
export const PT_CERT_STATUS_SKIN: Record<PtCertificateStatus, PtStatusSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  issued: { label: 'Issued', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  rejected: { label: 'Rejected', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

export function ptCertStatusSkin(value: string | null | undefined): PtStatusSkin {
  return value && Object.prototype.hasOwnProperty.call(PT_CERT_STATUS_SKIN, value)
    ? PT_CERT_STATUS_SKIN[value as PtCertificateStatus]
    : PT_UNKNOWN_SKIN;
}

/**
 * The certificate's kind, as the API decided it from the tests' results. A
 * kind is a statement about the well, so it reuses the result hues: a pass
 * green, a failure red. An unknown value is a slate `—`, never a guessed pass.
 */
export const PT_CERT_KIND_SKIN: Record<PtCertificateKind, PtStatusSkin> = {
  pass: { label: 'Pass', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  failure: { label: 'Failure', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

export function ptCertKindSkin(value: string | null | undefined): PtStatusSkin {
  return value && Object.prototype.hasOwnProperty.call(PT_CERT_KIND_SKIN, value)
    ? PT_CERT_KIND_SKIN[value as PtCertificateKind]
    : PT_UNKNOWN_SKIN;
}

/** Where a flag came from. The drafting agent's notes are labelled as AI wherever they are shown. */
export function ptFlagSourceLabel(source: string | null | undefined): string {
  if (source === 'ai') return 'AI note';
  if (source === 'rule') return 'Check';
  return '—';
}

// ─── PT-5 (fieldiq-api#932): rates, ticket mode, field tickets ───────────────

/** The ticket mode, exhaustive. An unknown or absent mode is `—`, never a guessed "Per job". */
export const PT_TICKET_MODE_LABEL: Record<PtTicketMode, string> = {
  per_job: 'Per job',
  per_day: 'Per day',
};

export function ptTicketModeLabel(value: string | null | undefined): string {
  return value && Object.prototype.hasOwnProperty.call(PT_TICKET_MODE_LABEL, value)
    ? PT_TICKET_MODE_LABEL[value as PtTicketMode]
    : '—';
}

/** What each kind of rate or line is called on the rate card and on a ticket. Exhaustive. */
export const PT_LINE_KIND_LABEL: Record<PtLineKind, string> = {
  test: 'Test',
  labour_hour: 'Labour',
  equipment_day: 'Equipment (day)',
  equipment_hour: 'Equipment (hour)',
  mobilization: 'Mobilization',
  mileage_km: 'Mileage',
  standby_hour: 'Standby',
  consumable: 'Consumable',
  other: 'Other',
};

export function ptLineKindLabel(value: string | null | undefined): string {
  return value && Object.prototype.hasOwnProperty.call(PT_LINE_KIND_LABEL, value) ? PT_LINE_KIND_LABEL[value as PtLineKind] : '—';
}

/**
 * The ticket status, exhaustive over PT_TICKET_STATUSES. Draft amber (waiting
 * on the company man's signature), signed blue (waiting on the office),
 * approved and invoiced green (both settled, the label tells them apart), void
 * a red badge on a steel rail — out of play, like a cancelled job.
 */
export const PT_TICKET_STATUS_SKIN: Record<PtTicketStatus, PtStatusSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  signed: { label: 'Signed', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  invoiced: { label: 'Invoiced', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  void: { label: 'Void', fill: 'bg-fi-signal-red', rail: 'border-l-fi-steel' },
};

export function ptTicketStatusSkin(value: string | null | undefined): PtStatusSkin {
  return value && Object.prototype.hasOwnProperty.call(PT_TICKET_STATUS_SKIN, value)
    ? PT_TICKET_STATUS_SKIN[value as PtTicketStatus]
    : PT_UNKNOWN_SKIN;
}

/**
 * A line's money column, from the API's own `money` verdict — never from a
 * client-side quantity × rate. "No rate" and "Enter the quantity" are gaps,
 * "No charge" is an explicit 0, and an unknown verdict is `—`.
 */
export function ptLineAmountText(line: Pick<PtTicketLine, 'money' | 'amount'>, currency: string | null | undefined): string {
  switch (line.money) {
    case 'priced':
      return fmtCurrency(line.amount, currency);
    case 'no_charge':
      return 'No charge';
    case 'no_quantity':
      return 'Enter the quantity';
    case 'no_rate':
      return 'No rate';
    default:
      return '—';
  }
}

/** True when the API says the line still needs something before a sign. */
export const ptLineNeedsAttention = (line: Pick<PtTicketLine, 'money'>) => line.money === 'no_rate' || line.money === 'no_quantity';

/** A rate as "CA$450.00/hour"; an absent rate is "No rate", never $0. */
export function ptRateText(rate: number | null | undefined, currency: string | null | undefined, unit: string | null | undefined): string {
  if (rate === null || rate === undefined) return 'No rate';
  return fmtCurrency(rate, currency, 2, unit ? `/${unit}` : undefined);
}

/** "2.5 hour", "20 L"; an absent quantity is `—`. */
export function ptQtyText(quantity: number | null | undefined, unit: string | null | undefined): string {
  if (quantity === null || quantity === undefined || !Number.isFinite(Number(quantity))) return '—';
  const n = Number(quantity).toLocaleString('en-US', { maximumFractionDigits: 2 });
  return unit ? `${n} ${unit}` : n;
}

/**
 * The ticket's subtotal as the API holds it. NULL means a live line is still
 * unpriced ("No rate"), or the ticket has no lines at all (`—`). The figure is
 * the API's; nothing here adds lines up.
 */
export function ptSubtotalText(t: Pick<PtTicket, 'subtotal' | 'currency' | 'line_count'>): string {
  if (t.subtotal !== null && t.subtotal !== undefined) return fmtCurrency(t.subtotal, t.currency);
  return t.line_count > 0 ? 'No rate' : '—';
}

// ─── PT-6 (fieldiq-api#939): invoices ────────────────────────────────────────

/**
 * pt_invoices.status (draft | sent | approved | paid | void), the downhole
 * vocabulary and its hues: draft amber (not yet with the customer), sent and
 * approved blue (out with the customer, not paid — the label tells them
 * apart), paid green, void slate on a transparent rail (claims no money).
 */
export const PT_INVOICE_STATUS_SKIN: Record<PtInvoiceStatus, PtStatusSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  sent: { label: 'Sent', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  paid: { label: 'Paid', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  void: { label: 'Void', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' },
};

export function ptInvoiceStatusSkin(value: string | null | undefined): PtStatusSkin {
  return value && Object.prototype.hasOwnProperty.call(PT_INVOICE_STATUS_SKIN, value)
    ? PT_INVOICE_STATUS_SKIN[value as PtInvoiceStatus]
    : PT_UNKNOWN_SKIN;
}

/** The roles the API's PT_INVOICE_WRITE allows (generate, recipient, send, void). */
export const PT_INVOICE_WRITE_ROLES = ['admin', 'office_staff'] as const;
export const canWritePtInvoices = (role: string | null | undefined) =>
  !!role && (PT_INVOICE_WRITE_ROLES as readonly string[]).includes(role);

/** The fallback reasons on which the API refuses Send (TAX_FALLBACK_BLOCKED). */
export const PT_SEND_BLOCKING_FALLBACK = ['jurisdiction_absent', 'not_yet_effective'];

/**
 * Where the invoice's tax came from, from the provenance the API stored at
 * generation — never re-derived. Every source and fallback reason the shared
 * resolver can write is named; anything else is `—`.
 */
export function ptTaxProvenanceText(
  inv: Pick<PtInvoice, 'tax_rate_source' | 'tax_fallback_reason' | 'jurisdiction' | 'tax_rate_effective_from' | 'tax_exempt'>,
): string {
  const where = inv.jurisdiction || 'the province';
  if (inv.tax_exempt) return 'Customer is tax exempt';
  switch (inv.tax_rate_source) {
    case 'jurisdiction_row': {
      const from = formatCalendarDate(inv.tax_rate_effective_from);
      return from ? `${where} rate, in effect from ${from}` : `${where} rate`;
    }
    case 'company_columns':
      switch (inv.tax_fallback_reason) {
        case 'no_rows':
          return 'Company tax settings';
        case 'jurisdiction_unknown':
          return 'Company tax settings (no province on the job)';
        case 'jurisdiction_absent':
          return `No ${where} rate set up — company default used`;
        case 'not_yet_effective':
          return `${where} rate not in effect yet — company default used`;
        default:
          return 'Company tax settings';
      }
    case 'none':
      return 'No tax applies';
    default:
      return '—';
  }
}

/** "GST (5%)", from the stored label and percent (fmtTaxRate); `—` without a label. */
export function ptTaxRowLabel(label: string | null | undefined, percent: number | null | undefined): string {
  if (!label) return '—';
  return percent === null || percent === undefined ? label : `${label} (${fmtTaxRate(percent)})`;
}
